import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AUDIENCE_LABELS,
  ORG_TYPE_LABELS,
  USER_CREATABLE_ORG_TYPES,
  audiencesFor,
  type Audience,
  type AudienceScope,
  type OrgType,
} from '@ready/shared';
import { useApi, type ContentOwner } from '@/lib/api';
import { Field, Input, Select } from '@/components/ui';

/*
 * "Who can see this" for courses, quizzes and workshops: the audience, plus
 * the groups, child organizations or countries and organization types it
 * names. The API checks the same rule (services/audience.ts).
 */

const HINTS: Record<Audience, string> = {
  public: 'Anyone with the link can see and take it; listed for your members.',
  org: 'Members of this organization and of every organization under it.',
  org_only: 'Members of this organization itself, not of the organizations under it.',
  groups: 'Only members of the groups you pick.',
  children: 'Only members of the organizations you pick, and those under them.',
  targeted:
    'Members of any organization in these countries and of these types, e.g. every university in India.',
};

const countryName = (() => {
  try {
    const names = new Intl.DisplayNames(['en'], { type: 'region' });
    return (code: string) => names.of(code) ?? code;
  } catch {
    return (code: string) => code;
  }
})();

export function AudiencePicker({
  owner,
  audience,
  scope,
  onChange,
  id,
  label = 'Who can see this',
  extraOptions = [],
  extraHint,
}: {
  owner: ContentOwner;
  audience: string;
  scope: AudienceScope | null;
  onChange: (audience: string, scope: AudienceScope | null) => void;
  id: string;
  label?: string;
  /** More choices after the audiences, e.g. a quiz's "Only inside a course". */
  extraOptions?: [value: string, label: string][];
  /** Hint for an extra option when it's picked. */
  extraHint?: string;
}) {
  const api = useApi();
  const options = audiencesFor(owner.kind);
  const known = (options as string[]).includes(audience);
  const needsOptions = owner.kind === 'org' && (audience === 'groups' || audience === 'children');
  const named = useQuery({
    queryKey: ['audienceOptions', owner.kind === 'org' ? owner.orgId : 'library'],
    queryFn: () => api.audienceOptions(owner.kind === 'org' ? owner.orgId : ''),
    enabled: needsOptions,
  });
  const s = scope ?? {};
  const toggle = (key: 'groupIds' | 'orgIds', value: string, on: boolean) => {
    const list = new Set(s[key] ?? []);
    if (on) list.add(value);
    else list.delete(value);
    onChange(audience, { ...s, [key]: [...list] });
  };

  return (
    <div className="space-y-3">
      <Field
        label={label}
        htmlFor={id}
        hint={
          !known
            ? extraHint
            : audience === 'public' && owner.kind === 'library'
              ? 'Anyone can find it on Explore and take it.'
              : HINTS[audience as Audience]
        }
      >
        <Select id={id} value={audience} onChange={(e) => onChange(e.target.value, null)}>
          {options.map((a) => (
            <option key={a} value={a}>
              {AUDIENCE_LABELS[a]}
            </option>
          ))}
          {extraOptions.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </Select>
      </Field>

      {audience === 'groups' && (
        <Choices
          legend="Groups"
          empty="This organization has no groups yet. Admins create them under Groups."
          loading={named.isPending}
          items={named.data?.groups.map((g) => ({ id: g.id, label: g.name })) ?? []}
          picked={s.groupIds ?? []}
          onToggle={(v, on) => toggle('groupIds', v, on)}
        />
      )}
      {audience === 'children' && (
        <Choices
          legend="Child organizations"
          empty="This organization has no child organizations."
          loading={named.isPending}
          items={
            named.data?.children.map((o) => ({
              id: o.id,
              label: `${o.depth > 1 ? '↳ ' : ''}${o.name}`,
            })) ?? []
          }
          picked={s.orgIds ?? []}
          onToggle={(v, on) => toggle('orgIds', v, on)}
        />
      )}
      {audience === 'targeted' && (
        <Targeted scope={s} onChange={(next) => onChange(audience, next)} />
      )}
    </div>
  );
}

function Choices({
  legend,
  empty,
  loading,
  items,
  picked,
  onToggle,
}: {
  legend: string;
  empty: string;
  loading: boolean;
  items: { id: string; label: string }[];
  picked: string[];
  onToggle: (id: string, on: boolean) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-1 text-sm font-medium">{legend}</legend>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <div className="grid gap-1 sm:grid-cols-2">
          {items.map((i) => (
            <label key={i.id} className="flex items-center gap-2 py-1 text-sm">
              <input
                type="checkbox"
                className="size-5 accent-primary"
                checked={picked.includes(i.id)}
                onChange={(e) => onToggle(i.id, e.target.checked)}
              />
              {i.label}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/** Countries (ISO codes) and organization types, for Nirvik's campaigns. */
function Targeted({
  scope,
  onChange,
}: {
  scope: AudienceScope;
  onChange: (scope: AudienceScope) => void;
}) {
  const [text, setText] = useState((scope.countries ?? []).join(', '));
  const codes = text
    .split(/[\s,;]+/)
    .map((c) => c.trim().toUpperCase())
    .filter(Boolean);
  const valid = codes.filter((c) => /^[A-Z]{2}$/.test(c));
  return (
    <div className="space-y-3">
      <Field
        label="Countries"
        htmlFor="audience-countries"
        hint={
          valid.length
            ? valid.map(countryName).join(', ')
            : 'Two-letter codes, e.g. IN, US. Leave empty for every country.'
        }
      >
        <Input
          id="audience-countries"
          value={text}
          placeholder="IN"
          onChange={(e) => {
            setText(e.target.value);
            const next = e.target.value
              .split(/[\s,;]+/)
              .map((c) => c.trim().toUpperCase())
              .filter((c) => /^[A-Z]{2}$/.test(c));
            onChange({ ...scope, countries: [...new Set(next)] });
          }}
        />
      </Field>
      <fieldset>
        <legend className="mb-1 text-sm font-medium">Organization types</legend>
        <p className="mb-1 text-xs text-muted-foreground">Leave all unchecked for every type.</p>
        <div className="grid gap-1 sm:grid-cols-3">
          {USER_CREATABLE_ORG_TYPES.map((t) => (
            <label key={t} className="flex items-center gap-2 py-1 text-sm">
              <input
                type="checkbox"
                className="size-5 accent-primary"
                checked={(scope.orgTypes ?? []).includes(t)}
                onChange={(e) => {
                  const list = new Set<OrgType>(scope.orgTypes ?? []);
                  if (e.target.checked) list.add(t);
                  else list.delete(t);
                  onChange({ ...scope, orgTypes: [...list] });
                }}
              />
              {ORG_TYPE_LABELS[t]}
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
